'use client'

// Std editor surface — MODERN skin (2026-07: modal reorganization). The
// pre-modal byte-frozen class strings live in git history; this skin now
// styles the modern column layout, the shared Configure/Attachments modals,
// and the smooth namekey object. Restyle the std editor by editing THESE
// tokens. The namekey keeps its legacy visual language (yellow field, @ sign,
// verdict colors) on modern geometry.

import { EditorSurfaceProvider, type EditorSkin, type EditorSurfaceValue } from '@/deskUiSkeletons/editor/EditorSurfaceContext'
import { editorAttachmentsUiSkin, editorModalSkin, modernSettingsSkin } from '@/msgComponents/skins/editorModalSkin'
import { RICH_EDIT_POLICY, STD_RICH_EDIT_VARIANT, stdRichEditSkin } from './richEdit.config'
import type { ReactNode } from 'react'

export const stdEditorSkin: EditorSkin = {
  headShort: '!hidden',
  head: {
    root: 'flex flex-col gap-3 border-b border-[#e2e8ee] bg-[#fbfcfd] p-4',
    nameKeyRow: 'flex min-w-0 flex-col gap-1.5',
    fromLabel: '',
    recipientsRow: 'flex min-w-0 items-center gap-1.5',
    subjectRow: 'flex min-w-0 flex-col gap-1 sm:flex-row sm:items-center sm:gap-2',
    subjectLabel: 'flex-none text-[13px] text-[#687482]',
    subjectInputBox: 'min-w-0 flex-1 rounded-md border border-[#d9e1e8] bg-white transition-colors focus-within:border-[#2563eb]',
    subjectInput: '!m-0 block !w-full box-border !rounded-md !border-0 !bg-transparent !px-3 !py-2 text-[13px] text-[#17212b] shadow-none outline-none',
    compatControl: 'hidden pointer-events-none',
  },
  nameKey: {
    block: 'flex w-full flex-col gap-1.5',
    moreButton: '',
    advancedOpen: 'block',
    advancedClosed: 'hidden',
    switchRow: 'flex items-center justify-between gap-2',
    switchButton: 'cursor-pointer self-start rounded-md px-2 py-1 text-xs text-[#2563eb] hover:bg-[#edf2f6]',
  },
  smartName: {
    root: 'flex w-full min-w-0 items-center text-xs',
    sign: 'flex h-9 flex-none cursor-default items-center rounded-l-md border border-r-0 border-[#e3ddb9] bg-[#fffbe6] px-2.5 text-[#8a8567] transition-colors',
    signStatus: { busy: 'text-[#17212b]', ok: 'text-green-700', wrong: 'text-red-600' },
    inputBox: 'relative flex h-9 min-w-0 flex-1 items-center overflow-hidden rounded-r-md border border-l-0 border-[#e3ddb9] bg-[#fffbe6] pr-1 transition-colors focus-within:border-[#2563eb]',
    inputBoxWrong: 'border-[#cc0000]',
    input: '!m-0 h-6 !w-full !rounded-none !border-0 !bg-transparent !p-0 !px-1 text-[13px] text-[#17212b] shadow-none outline-none placeholder:text-[#9a9a85]',
    actions: 'group relative flex h-9 flex-none items-center pl-1.5',
    check: 'block cursor-pointer rounded-md px-2 py-1.5 text-[#2563eb] hover:bg-[#edf2f6]',
    quick: 'absolute right-0 top-full z-[9999999] hidden group-hover:block group-focus-within:block',
    quickItems: 'w-44 rounded-md border border-[#d9e1e8] bg-white p-1 text-xs shadow-lg',
    quickItem: 'mb-0.5 block cursor-pointer rounded px-2 py-1 text-[#314253] hover:bg-[#edf2f6]',
  },
  extName: {
    root: 'flex flex-col gap-1.5 text-xs',
    quickRow: 'flex flex-wrap items-center gap-1',
    label: 'px-1 py-0.5 text-[#687482]',
    quickBtn: 'cursor-pointer rounded bg-[#f2f4f6] px-1.5 py-1 text-xs text-[#314253] hover:bg-[#e4e9ee] hover:underline',
    subKeyBtn: 'cursor-pointer rounded bg-[#2563eb] px-1.5 py-1 text-xs text-white hover:bg-[#1d4ed8]',
    box: 'relative w-full max-w-full overflow-hidden rounded-md border border-[#e3ddb9] bg-[#fffbe6] pr-8 transition-colors focus-within:border-[#2563eb]',
    textarea: '!m-0 block min-h-[38px] !w-full resize-none !rounded-none !border-0 !bg-transparent !p-2 !pb-1 text-[13px] leading-[150%] text-[#17212b] outline-none',
    mirror: 'hidden',
    checkRow: 'flex flex-wrap items-center gap-1.5',
    subKeyText: 'block rounded bg-[#2563eb] px-1.5 py-0.5 text-xs text-white empty:hidden',
    check: 'cursor-pointer rounded px-1.5 py-0.5 text-xs text-[#2563eb] hover:bg-[#edf2f6]',
    emailInfo: 'cursor-default px-1 py-0.5 text-[11px] text-[#687482]',
    emailEdit: 'cursor-pointer px-1 py-0.5 text-[11px] text-[#2563eb] underline',
    code: 'hidden flex-wrap items-center gap-2 text-xs',
    codeInput: 'h-8 w-full max-w-[250px] rounded border border-[#c8d2dc] bg-white px-2 text-right text-xs',
    codeBtn: 'cursor-pointer rounded px-1.5 py-0.5 text-[#2563eb] hover:bg-[#edf2f6]',
    codeInfo: 'text-[11px] text-[#687482]',
  },
  recipients: {
    root: 'flex w-full min-w-0 flex-col gap-1 text-xs sm:flex-row sm:items-center sm:gap-2',
    label: 'flex-none text-[13px] text-[#687482]',
    field: 'flex min-w-0 flex-1 flex-wrap items-center gap-1 rounded-md border border-[#d9e1e8] bg-white px-1 pr-8 transition-colors focus-within:border-[#2563eb]',
    inputWrap: 'min-w-[60px] flex-1 overflow-hidden',
    input: '!mt-0 block !w-full !rounded-none !border-0 !bg-transparent !p-2 text-[13px] text-[#17212b] shadow-none outline-none',
    inputErr: '!text-[#b91c1c] placeholder:text-[#b91c1c]',
    inputErrHint: 'flex-none self-center whitespace-nowrap pr-2 text-[11px] text-[#b91c1c]',
    list: 'my-1 ml-1 flex flex-wrap items-center gap-1',
    token: 'flex h-6 cursor-pointer items-center rounded-full px-2.5 leading-6 text-[#404040]',
    tokenKind: { email: 'bg-[lightblue]', msg: 'bg-[wheat]', self: 'bg-[#2563eb]' },
    tokenText: 'max-h-5 text-xs font-semibold tracking-[0.3px] text-[#314253]',
    tokenTextSelf: 'text-white',
    tokenX: 'ml-1.5 cursor-pointer font-bold text-[#888888] hover:text-red-600',
    tokenXSelf: 'text-[#d3d3d3]',
    tokenLocked: '!cursor-default',
    compat: 'hidden',
  },
  settings: modernSettingsSkin,
  body: {
    root: 'flex min-h-0 flex-1 flex-col gap-2 p-4',
    tools: 'flex flex-wrap items-center gap-2',
    toolButton: 'cursor-pointer rounded-md border border-[#d9e1e8] bg-white px-2.5 py-1.5 text-[13px] text-[#314253] hover:bg-[#edf2f6]',
    toolButtonActive: 'cursor-pointer rounded-md border border-[#2563eb] bg-[#e8f0ff] px-2.5 py-1.5 text-[13px] text-[#2563eb]',
    legacyToolbarHidden: '',
    richFoldWrap: '',
    richFoldButton: '',
    textWrap: 'min-h-full',
    textInner: 'min-h-full',
    textarea: 'box-border min-h-[300px] w-full resize-y rounded-md border border-[#d9e1e8] bg-white px-3 py-2.5 text-[13px] leading-[1.5] text-[#17212b] outline-none transition-colors focus:border-[#2563eb]',
    richSurface: 'box-border min-h-[300px] w-full overflow-auto rounded-md border border-[#d9e1e8] bg-white px-3 py-2.5 text-[13px] leading-[1.5] text-[#17212b] outline-none transition-colors focus:border-[#2563eb]',
    richToolbar: 'flex flex-wrap items-center gap-1',
    richButton: 'h-8 min-w-8 cursor-pointer rounded-md border border-[#d9e1e8] bg-white px-2 text-[12px] leading-none text-[#314253] hover:bg-[#edf2f6] active:bg-[#e2e9ef]',
    editPinWarning: 'rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[13px] text-amber-900',
  },
  modal: editorModalSkin,
  attachmentsUi: editorAttachmentsUiSkin,
}

const stdEditorSurface: EditorSurfaceValue = {
  variant: 'std',
  skin: stdEditorSkin,
  richEditVariant: STD_RICH_EDIT_VARIANT,
  richEditPolicy: RICH_EDIT_POLICY,
  richEditSkin: stdRichEditSkin,
  richEditDefaultFormat: 'html',
}

export function StdEditorSurfaceProvider({ children }: { children: ReactNode }) {
  return <EditorSurfaceProvider value={stdEditorSurface}>{children}</EditorSurfaceProvider>
}
